import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

let audioCtx: AudioContext | null = null;
let unlockWired = false;

type AudioContextCtor = typeof AudioContext;

function AudioContextClass(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const Ctor = AudioContextClass();
    if (!Ctor) return null;
    try {
      audioCtx = new Ctor();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

/** Resume AudioContext after a user gesture so move sounds are not silent. */
export async function unlockAudio(): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      /* ignore */
    }
  }
}

/** Wire once: unlock Web Audio on first pointer/touch/click. */
export function ensureAudioUnlockListener(): void {
  if (typeof window === 'undefined' || unlockWired) return;
  unlockWired = true;
  const unlock = () => {
    void unlockAudio();
  };
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('touchstart', unlock, { once: true, passive: true });
  window.addEventListener('click', unlock, { once: true, passive: true });
}

async function withUnlockedAudio(play: (ctx: AudioContext) => void): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return;
    }
  }
  play(ctx);
}

/** Short click for pawn/wall moves (Web Audio, no asset files). */
export function playMoveSound() {
  void withUnlockedAudio((ctx) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 440;
    gain.gain.value = 0.09;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.09, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    osc.start(t);
    osc.stop(t + 0.09);
  });
}

export function playWinSound() {
  void withUnlockedAudio((ctx) => {
    [523, 659, 784].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      gain.gain.value = 0.08;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.08, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.start(t);
      osc.stop(t + 0.22);
    });
  });
}

async function nativeImpact(style: ImpactStyle): Promise<void> {
  try {
    await Haptics.impact({ style });
  } catch {
    /* plugin unavailable */
  }
}

function webVibrate(ms: number): void {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(ms);
  }
}

export function vibrateOnTurn() {
  if (Capacitor.isNativePlatform()) {
    void nativeImpact(ImpactStyle.Medium);
    return;
  }
  webVibrate(40);
}

/** Short haptic for own pawn/wall placement. */
export function vibrateOnMove() {
  if (Capacitor.isNativePlatform()) {
    void nativeImpact(ImpactStyle.Light);
    return;
  }
  webVibrate(18);
}

if (typeof window !== 'undefined') {
  ensureAudioUnlockListener();
}
